/* eslint-disable sort-keys */
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { Tab, Tabs, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { Box } from '@mui/system'
import { DataService } from 'api/DataService'
import useReport from 'context/ReportContext'

import { FormCompanyScope } from 'components/CompanySelector'
import EmptyState from 'components/EmptyState/EmptyState'
import { Table } from 'components/Table'
import { TableSkeleton } from 'components/Table/TableSkeleton'

import { theme } from 'styles/theme'

import FinancialStatementForm from './components/FinancialStatementForm'
import * as S from './styles'
import { convertToRows } from './utils'

export type ReportFilterEntry = {
  key: string
  label: string
  display?: string
  uuid?: string
  amount?: string
  flag?: boolean
}

export type ReportResponse = {
  created_at: Date
  downloaded: boolean
  filters: string
  filter_by: string
  filters_parsed?: ReportFilterEntry[]
  merchant_uuid: string
  model:
    | 'Billing'
    | 'Receivable'
    | 'Customer'
    | 'Transfer'
    | 'Contract'
    | 'Nfse'
    | 'Installment'
    | 'StatementDatePaid'
    | 'StatementExpectedOn'
    | 'customer_sheet'
    | 'CustomerSheet'
  status: string
  uuid: string
}

type ContentType = 'emitir' | 'emitidos'

type ReportsProps = {
  hideHeader?: boolean
}

export const Reports = ({ hideHeader = false }: ReportsProps = {}) => {
  const [loading, setLoading] = useState(true)
  const [reports, setReports] = useState<ReportResponse[]>([])
  const [contentType, setContentType] = useState<ContentType>('emitidos')
  const isMobile = useMediaQuery('(max-width:800px)')
  const isMobileSmall = useMediaQuery('(max-width:600px)', { noSsr: false })
  const [tableProps, setTableProps] = useState({
    page: 0,
    rowsPerPage: 25,
    totalCount: 0
  })
  const hasLoadedInitially = useRef(false)

  const { refreshKey } = useReport()

  const fetchReports = useCallback(async () => {
    setLoading(true)
    const resp = await DataService({
      type: 'GET',
      url: `/v1/reports?per=${tableProps.rowsPerPage}&page=${
        tableProps.page + 1
      }`
    })
    const data: ReportResponse[] = (await resp.data?.records) || []
    if (data.length > 0) {
      const reportsMapped = data.map((el) => ({
        ...el,
        ...JSON.parse(el.filters)
      }))
      setReports(reportsMapped)

      setTableProps((prevState) => ({
        ...prevState,
        totalCount: Number(resp.data.meta.total_count)
      }))
    } else {
      setReports([])
    }
    setLoading(false)
  }, [tableProps.rowsPerPage, tableProps.page])

  useEffect(() => {
    fetchReports()
  }, [tableProps.page, tableProps.rowsPerPage, fetchReports])

  useEffect(() => {
    if (contentType === 'emitidos' && hasLoadedInitially.current) {
      fetchReports()
    }
    if (contentType === 'emitidos') {
      hasLoadedInitially.current = true
    }
  }, [contentType, fetchReports])

  useEffect(() => {
    if (refreshKey > 0 && contentType === 'emitidos') {
      fetchReports()
    }
  }, [refreshKey, contentType, fetchReports])

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number
  ) => {
    setTableProps((s) => ({ ...s, page }))
  }

  const handleChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const rowsPerPage = Number(e.target.value || 25)
    setTableProps((s) => ({ ...s, rowsPerPage }))
  }

  return (
    <S.PageWrapper>
      {!isMobileSmall && !hideHeader && (
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap="8px"
          mb="16px"
        >
          <Typography fontSize={'1.5rem'} variant="smBold">
            Relatórios
          </Typography>
          <Tooltip
            enterTouchDelay={0}
            arrow
            placement="right"
            title="Os relatórios são deletados automaticamente depois de 30 dias."
          >
            <Box display={'flex'} justifyContent={'top'}>
              <Icon
                icon="info"
                sx={{
                  alignSelf: 'end',
                  marginTop: '15%',
                  fontSize: '1.2rem',
                  color: theme.palette.neutral[700]
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      )}

      <Tabs
        sx={{
          bgcolor: theme.palette.neutral[900],
          borderRadius: '12px 12px 0 0',
          width: isMobile ? '100%' : 'fit-content',
          '& .MuiTabs-indicator': { bgcolor: theme.palette.primary.main }
        }}
        value={contentType}
        onChange={(_, type) => setContentType(type)}
        textColor="inherit"
        indicatorColor="secondary"
        aria-label="tabs"
      >
        <Tab
          sx={{
            '& :not(.Mui-selected)': {
              opacity: 1
            },
            ...(isMobile && {
              width: '50%'
            })
          }}
          value={'emitidos' as ContentType}
          label={
            <Typography
              textTransform="lowercase"
              display="flex"
              variant="xsBold"
              p="0 24px"
            >
              <Typography textTransform="uppercase" variant="xsBold">
                R
              </Typography>
              elatórios emitidos
            </Typography>
          }
        />
        <Box borderRight="1px solid #E3E5E3"></Box>
        <Tab
          sx={{
            '& :not(.Mui-selected)': {
              opacity: 1
            },
            ...(isMobile && {
              width: '50%'
            })
          }}
          value={'emitir' as ContentType}
          label={
            <Typography
              textTransform="lowercase"
              display="flex"
              variant="xsBold"
              p="0 24px"
            >
              <Typography textTransform="uppercase" variant="xsBold">
                E
              </Typography>
              xtrato Financeiro
            </Typography>
          }
        />
      </Tabs>

      <S.ContentWrapper>
        {contentType === 'emitir' && (
          // SC-10999: na visão multi-empresas o extrato sai da empresa
          // escolhida (sem isso, sairia da empresa de contexto sem aviso)
          <FormCompanyScope>
            {(companySelector) => (
              <FinancialStatementForm companySelector={companySelector} />
            )}
          </FormCompanyScope>
        )}

        {contentType === 'emitidos' && (
          <>
            <S.TableWrapper>
              {loading ? (
                <TableSkeleton />
              ) : (
                <>
                  {reports.length > 0 ? (
                    <Table
                      mobileCards
                      handleChangePage={handleChangePage}
                      handleChangeRowsPerPage={handleChangeRowsPerPage}
                      header={[
                        { id: 'type', label: 'Tipo' },
                        { id: 'due_date', label: 'Data/hora solicitação' },
                        { id: 'status', label: 'Status' },
                        { id: 'actions', label: '' }
                      ]}
                      rows={convertToRows(reports)}
                      select={{ useSelect: false }}
                      {...tableProps}
                    />
                  ) : (
                    <Box display="flex" justifyContent="center">
                      <Box
                        maxWidth={{ xs: '100%', md: '60%' }}
                        width="100%"
                        mt="18vh"
                      >
                        <EmptyState
                          title="Nenhum relatório disponível"
                          message="Os relatórios gerados aparecerão aqui."
                        />
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </S.TableWrapper>
          </>
        )}
      </S.ContentWrapper>
    </S.PageWrapper>
  )
}

export default Reports
